import {
  Card,
  Col,
  Form,
  Modal,
  notification,
  Popconfirm,
  Row,
  Select,
} from 'antd';
import useForm from 'antd/lib/form/hooks/useForm';
import ButtonOperation from 'components/ButtonOperation';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import DeleteButton from 'components/DeleteButton';
import UpdateButton from 'components/UpdateButtonAction';
import { useFormList } from 'hooks/useFormList';
import { CloseOutlined, DeleteTwoTone } from '@ant-design/icons';

import React, { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  addAssetCatalogOptionConflict,
  getAssetCatalogOptionConflictList,
  removeAssetCatalogOptionConflict,
} from 'services/assetconfig';
import { indexBy } from 'utils/utils2';

export type CatalogOptionsConflictsModalProps = {
  visible: boolean;
  onCancel: () => void;
  catalogOption: any[];
  catalogid: number;
  optid: number;
};

const CatalogOptionsConflictsModal = ({
  onCancel,
  visible,
  catalogOption,
  catalogid,
  optid,
}: CatalogOptionsConflictsModalProps) => {
  const fieldName = 'conflictsList';
  const NEW_CATALOG_OPTION_CONFLI_ID = -1;

  const [form] = useForm();

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'confid',
    addAsFirst: true,
  });

  const {
    data: conflictList,
    refetch,
    isFetching,
  } = useQuery(['getAssetCatalogOptionLabelList', { catalogid, optid }], () =>
    getAssetCatalogOptionConflictList({ catalogid, optid }).then(
      (res) => res?.list || []
    )
  );

  const addRow = () => {
    const row = {
      catalogid: 0,
      confid: NEW_CATALOG_OPTION_CONFLI_ID,
      optid: 0,
      optidconflict: '' as any,
    };
    add(row);
  };

  useEffect(() => {
    if (conflictList) {
      form.setFieldsValue({
        [fieldName]: conflictList,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, conflictList]);

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const relation: any[] = form.getFieldValue(fieldName);
      const index = indexBy(relation, 'confid')[record.confid];
      const currentValue = {
        ...relation[index],
      };

      const { labelid } = currentValue;
      addAssetCatalogOptionConflict({
        catalogid: catalogid,
        optid: optid,
        req: { ...currentValue, optid: optid, catalogid: catalogid },
      }).then((response) => {
        if (response.code === '200') {
          notification.success({
            message: response.message,
          });
          refetch();
        }
      });
    },
    [form, catalogid, optid]
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'confid',
        width: 50,
        fixed: 'left',
      },
      {
        dataIndex: 'optconflictlabel',
        width: 150,
        render: (value, record) =>
          NEW_CATALOG_OPTION_CONFLI_ID === record?.confid ? ( 
            <Form.Item
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'confid')[record.confid],
                'optidconflict',
              ]}
              noStyle
            >
              <Select allowClear>
                {catalogOption?.map((e, i) => (
                  <Select.Option value={e?.optid} key={i}>
                    {e?.optcodelabel + ' - ' + e?.optvaluelabel}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            value
          ),
      },

      {
        dataIndex: 'action',
        width: 50,
        align: 'right',
        render: (value, record) => (
          <Row gutter={8} justify="center">
            {NEW_CATALOG_OPTION_CONFLI_ID === record?.confid && ( 
              <Col>
                <UpdateButton
                  onClick={(ev) => {
                    ev.preventDefault();
                    onSaveOrEdit(record);
                  }}
                  comcode="save"
                  disabled={record?.optidconflict === 0} 
                />
              </Col>
            )}
            <Col span={8}>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  NEW_CATALOG_OPTION_CONFLI_ID === record.confid 
                    ? remove(NEW_CATALOG_OPTION_CONFLI_ID)
                    : removeAssetCatalogOptionConflict({
                        catalogid: catalogid,
                        confid: record.confid,
                        optid: optid,
                      }).then((res) => {
                        notification.success({
                          message: res.message,
                        });
                        refetch();
                      });
                }}
              >
                <DeleteButton
                  onlyIcon
                  danger={NEW_CATALOG_OPTION_CONFLI_ID === record.confid} 
                  icon={
                    NEW_CATALOG_OPTION_CONFLI_ID === record.confid ? ( 
                      <CloseOutlined height="14px" width="14px" color="white" />
                    ) : (
                      <DeleteTwoTone twoToneColor="#eb2f96" />
                    )
                  }
                />
              </Popconfirm>
            </Col>
          </Row>
        ),
      },
    ],
    [form, conflictList, catalogOption]
  );

  return (
    (<Modal
      open={visible}
      onCancel={onCancel}
      width={1000}
      destroyOnClose
      footer={null}
    >
      <Card>
        <Form form={form}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const confliList: any[] = getFieldValue(fieldName);

              const isNewItemInTable =
                confliList?.find(
                  ({ confid }) => confid === NEW_CATALOG_OPTION_CONFLI_ID
                ) !== undefined;

              return (
                <DataGrid
                  rowKey="confid"
                  titleComcode="confliList"
                  dataSource={confliList}
                  columns={columns}
                  hideFilters
                  isLoading={isFetching}
                  extraActions={
                    <ButtonOperation
                      comcode="add"
                      type="primary"
                      htmlType="button"
                      disabled={isNewItemInTable}
                      onClick={() => {
                        addRow();
                      }}
                    />
                  }
                />
              );
            }}
          </Form.Item>
        </Form>
      </Card>
    </Modal>)
  );
};

export default CatalogOptionsConflictsModal;
